import { Message, useBroadcastChannelReceiver, useBroadcastChannelSender } from '@ocula/broadcast-channel';
import * as Portal from '@radix-ui/react-portal';
import { useThrottledEffect } from '@react-hookz/web/esm';
import { useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useRegisterSW } from 'virtual:pwa-register/react';

import { ReloadPrompt } from './ReloadPrompt';

/**
 * Registers the service worker and displays a reload prompt if the currently installed
 * service worker detects that there is a new version of the app available.
 */
export function ServiceWorkerUpdater() {
  const serviceWorkerRegistration = useRef<ServiceWorkerRegistration | undefined>();
  const { pathname } = useLocation();
  const postMessage = useBroadcastChannelSender();

  const {
    needRefresh: [needsRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(registration) {
      // eslint-disable-next-line no-console
      console.log(`SW registered: ${registration}`);
      serviceWorkerRegistration.current = registration;

      if (registration) {
        // https://vite-plugin-pwa.netlify.app/frameworks/react.html#periodic-sw-updates
        setInterval(
          () => {
            registration.update();
          },
          5 * 60 * 1000,
        ); // 5 minutes in milliseconds.
      }
    },
    onRegisterError(error) {
      // eslint-disable-next-line no-console
      console.error(error);
    },
  });

  // Check on every page navigation if the service worker has been updated, but throttled
  // so it's not too often.
  useThrottledEffect(
    () => {
      serviceWorkerRegistration.current?.update();
    },
    [pathname],
    15 * 1000, // 15 seconds in milliseconds
  );

  // Listen for swUpdated messages from any other open tabs that are running this app too.
  const serviceWorkerUpdatedHandler = useCallback((message: Message) => {
    if (message.type === 'swUpdated') {
      // Reload the page in order to get rid of the 'New content available' toast.
      // This doesn't happen automatically if the user clicks the reload button
      // in a different tab to this one: https://github.com/antfu/vite-plugin-pwa/issues/212
      globalThis.location.reload();
    }
  }, []);
  useBroadcastChannelReceiver(serviceWorkerUpdatedHandler);

  return (
    <Portal.Root>
      <ReloadPrompt
        needsRefresh={needsRefresh}
        onClick={async () => {
          await postMessage({ type: 'swUpdated' });
          await updateServiceWorker(true);
          // If the reload button fails to disappear on a tab, invoke a fallback reload
          // so that the user will be able to dismiss the 'New content available' toast.
          globalThis.location.reload();
        }}
      />
    </Portal.Root>
  );
}
